import { isUnauthorized } from '@portals-platform/api-client';
import { RequireAuth } from '@portals-platform/auth';
import { Skeleton } from '@portals-platform/ui';
import {
  createRootRoute,
  createRoute,
  createRouter,
  Navigate,
  Outlet,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { ErrorPage } from './components/ErrorPage.js';
import { LoadingPage } from './components/LoadingPage.js';
import { AdminDashboardPage } from './routes/AdminDashboardPage.js';
import { CallbackPage } from './routes/CallbackPage.js';
import { CertificateDetailsPage } from './routes/CertificateDetailsPage.js';
import { CertificatesPage } from './routes/CertificatesPage.js';
import { EditCertificatePage } from './routes/EditCertificatePage.js';
import { LoginPage } from './routes/LoginPage.js';
import { NewCertificatePage } from './routes/NewCertificatePage.js';
import { NotFoundPage } from './routes/NotFoundPage.js';
import { RenewalsPage } from './routes/RenewalsPage.js';
import { ReportsPage } from './routes/ReportsPage.js';
import { SettingsPage } from './routes/SettingsPage.js';
import { TeamPage } from './routes/TeamPage.js';

function AuthenticatedLayout() {
  const navigate = useNavigate();
  const location = useRouterState({ select: (s) => s.location });
  return (
    <RequireAuth
      onUnauthenticated={() =>
        void navigate({
          to: '/login',
          search: { returnTo: location.pathname + location.searchStr },
        })
      }
      fallback={<Skeleton className="h-screen w-full" />}
    >
      <Outlet />
    </RequireAuth>
  );
}

function AuthRouteError({ error, reset }: { error: Error; reset: () => void }) {
  if (isUnauthorized(error)) {
    return <Navigate to="/login" search={{ returnTo: undefined }} />;
  }
  return <ErrorPage error={error} reset={reset} />;
}

const rootRoute = createRootRoute({
  component: () => <Outlet />,
  notFoundComponent: NotFoundPage,
  errorComponent: ({ error, reset }) => <ErrorPage error={error} reset={reset} />,
  pendingComponent: LoadingPage,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
  validateSearch: (search: Record<string, unknown>) => ({
    returnTo: typeof search.returnTo === 'string' ? search.returnTo : undefined,
  }),
});

const callbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/callback',
  component: CallbackPage,
  validateSearch: (search: Record<string, unknown>) => ({
    error: typeof search.error === 'string' ? search.error : undefined,
    error_description:
      typeof search.error_description === 'string' ? search.error_description : undefined,
  }),
});

const authenticatedRoute = createRoute({
  id: '_authenticated',
  getParentRoute: () => rootRoute,
  component: AuthenticatedLayout,
  errorComponent: AuthRouteError,
});

const dashboardRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/',
  component: AdminDashboardPage,
});

const certificatesRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/certificates',
  component: CertificatesPage,
});

// Declared before the `$certificateId` route: a param route would otherwise match the
// literal segment "new" and swallow this page.
const newCertificateRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/certificates/new',
  component: NewCertificatePage,
});

const certificateDetailsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/certificates/$certificateId',
  component: CertificateDetailsPage,
});

const editCertificateRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/certificates/$certificateId/edit',
  component: EditCertificatePage,
});

const renewalsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/renewals',
  component: RenewalsPage,
});

const reportsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/reports',
  component: ReportsPage,
});

const teamRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/team',
  component: TeamPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings',
  component: SettingsPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  callbackRoute,
  authenticatedRoute.addChildren([
    dashboardRoute,
    certificatesRoute,
    newCertificateRoute,
    certificateDetailsRoute,
    editCertificateRoute,
    renewalsRoute,
    reportsRoute,
    teamRoute,
    settingsRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPendingMs: 300,
  defaultPendingMinMs: 500,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
